/*
 * Keeps the free gifts of the product offer section honest in the cart drawer and on the cart page.
 * custom-product-offer.js adds each gift with a private `_gift_for` line property that holds the variant IDs
 * of the bundles that unlock it. There is one of each gift per order, and it leaves the cart with the last of those bundles.
 * Any other $0 line counts as a gift without a bundle, so it is removed as well.
 * The cart forms mark the gift quantity fields with data-gift and data-gift-for
 * (snippets/cart-drawer.liquid, sections/main-cart-items.liquid).
 * Loaded by snippets/custom-gift-guard.liquid from the cart drawer and the cart page, after constants.js and pubsub.js.
 * A storefront safeguard only: links that go straight to checkout never load the theme.
 */
(function () {
  // The cart page can render the script twice (drawer and page), and it must run once
  if (window.customGiftGuard) return;
  window.customGiftGuard = true;

  var GIFT_PROPERTY = '_gift_for';
  var SOURCE = 'custom-gift-guard';
  var script = document.currentScript;
  var running = false;
  var again = false;
  var leaving = false;
  // Line keys of the last whole cart seen, in order, to spot a cart form painted from an older cart
  var knownKeys = null;

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function request(url, options) {
    return fetch(rootUrl() + url, options).then(function (response) {
      if (!response.ok) throw new Error('Cart request failed');
      return response.json();
    });
  }

  // /cart/add.js answers with the added lines only, so only a payload with item_count is the whole cart
  function isWholeCart(cart) {
    return !!cart && Array.isArray(cart.items) && typeof cart.item_count === 'number';
  }

  function remember(cart) {
    if (isWholeCart(cart)) {
      knownKeys = cart.items.map(function (item) {
        return item.key;
      });
    }
  }

  function quantityFields(form) {
    return Array.prototype.filter.call(form.elements, function (field) {
      return field.name === 'updates[]';
    });
  }

  // The cart forms send updates[] by position, so they are only safe while their lines match the cart
  function formMatches(form, keys) {
    var fields = quantityFields(form);
    if (!fields.length || !keys) return true;
    if (fields.length !== keys.length) return false;
    return fields.every(function (field, index) {
      return field.dataset.quantityLineKey === keys[index];
    });
  }

  function cartForms() {
    return Array.prototype.filter.call(document.forms, function (form) {
      return quantityFields(form).length > 0;
    });
  }

  // A gift can be tied to several bundles (comma-separated variant IDs): it stays while any of them is in the cart
  function ownersOf(value) {
    return value ? String(value).split(',') : [];
  }

  function isGift(item) {
    var owners = ownersOf(item.properties && item.properties[GIFT_PROPERTY]);
    return owners.length > 0 || item.original_price === 0;
  }

  function hasBundle(owners, bundles) {
    return owners.some(function (owner) {
      return bundles[owner];
    });
  }

  // Returns { lineKey: quantity } for the gift lines to correct, or null when the cart is fine.
  // One of each gift per order, and only while a bundle that unlocks it is in the cart.
  function fixesFor(cart) {
    var bundles = {};
    var kept = {};
    var updates = null;
    cart.items.forEach(function (item) {
      if (!isGift(item)) bundles[String(item.variant_id)] = true;
    });
    cart.items.forEach(function (item) {
      if (!isGift(item)) return;
      var owners = ownersOf(item.properties && item.properties[GIFT_PROPERTY]);
      var allowed = hasBundle(owners, bundles) && !kept[item.variant_id] ? 1 : 0;
      if (allowed) kept[item.variant_id] = true;
      if (item.quantity > allowed) {
        updates = updates || {};
        updates[item.key] = allowed;
      }
    });
    return updates;
  }

  // The cart forms post their quantity fields straight to /cart, often on the way to checkout,
  // so the gift fields are corrected in the submit event, before the browser reads the form
  function correctForm(form) {
    var fields = quantityFields(form);
    var bundles = {};
    fields.forEach(function (field) {
      if (!field.hasAttribute('data-gift') && (parseInt(field.value, 10) || 0) > 0) {
        bundles[field.dataset.quantityVariantId] = true;
      }
    });
    var kept = {};
    fields.forEach(function (field) {
      if (!field.hasAttribute('data-gift')) return;
      var variant = field.dataset.quantityVariantId;
      var allowed = hasBundle(ownersOf(field.dataset.giftFor), bundles) && !kept[variant] ? 1 : 0;
      if (allowed && (parseInt(field.value, 10) || 0) > 0) kept[variant] = true;
      if ((parseInt(field.value, 10) || 0) > allowed) field.value = String(allowed);
    });
  }

  // At most one reload every 30 seconds, so a cart that refuses the fix can't loop the page
  function reloadOnce() {
    var key = 'custom-gift-guard-reload';
    try {
      var last = parseInt(window.sessionStorage.getItem(key), 10) || 0;
      if (Date.now() - last < 30000) return false;
      window.sessionStorage.setItem(key, String(Date.now()));
      return true;
    } catch (error) {
      return false;
    }
  }

  function sectionContent(html, selector) {
    return html ? new DOMParser().parseFromString(html, 'text/html').querySelector(selector) : null;
  }

  // The focused cart control as a selector, the way Dawn finds it again after a re-render
  function focusedSelector(drawer) {
    var active = document.activeElement;
    if (!drawer || !active || !drawer.contains(active) || !active.closest) return null;
    var row = active.closest('.cart-item');
    return row && row.id && active.name ? '#' + row.id + ' [name="' + active.name + '"]' : null;
  }

  function restoreFocus(drawer, selector, isEmpty) {
    if (!drawer || !drawer.classList.contains('active') || typeof trapFocus !== 'function') return;
    var empty = drawer.querySelector('.drawer__inner-empty');
    if (isEmpty && empty) {
      trapFocus(empty, drawer.querySelector('a'));
      return;
    }
    var target = (selector && drawer.querySelector(selector)) || drawer.querySelector('.drawer__inner');
    if (target) trapFocus(drawer, target);
  }

  // Same refresh Dawn's cart.js does after a quantity change
  function refresh(cart) {
    var sections = cart.sections || {};
    var isEmpty = cart.item_count === 0;
    var bubble = document.getElementById('cart-icon-bubble');
    var freshBubble = sectionContent(sections['cart-icon-bubble'], '.shopify-section');
    if (bubble && freshBubble) bubble.innerHTML = freshBubble.innerHTML;

    var cartPage = document.querySelector('cart-items');
    if (cartPage) {
      // The cart page renders several sections around the lines, so a reload keeps them all in step,
      // unless a cart form is already on its way to checkout
      if (!leaving && reloadOnce()) {
        window.location.reload();
        return;
      }
      cartPage.classList.toggle('is-empty', isEmpty);
      var footer = document.getElementById('main-cart-footer');
      if (footer) footer.classList.toggle('is-empty', isEmpty);
    }

    var drawer = document.querySelector('cart-drawer');
    var inner = drawer && drawer.querySelector('.drawer__inner');
    var freshInner = sectionContent(sections['cart-drawer'], '.drawer__inner');
    var focused = focusedSelector(drawer);
    if (inner && freshInner) {
      drawer.classList.toggle('is-empty', isEmpty);
      inner.innerHTML = freshInner.innerHTML;
    }

    // Dawn's own subscribers re-render after this, so focus goes back once they are done
    var done = null;
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try {
        done = publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: cart });
      } catch (error) {}
    }
    var focus = function () {
      restoreFocus(drawer, focused, isEmpty);
    };
    Promise.resolve(done).then(focus, focus);
  }

  // Dawn's own loading state, so nobody acts on the rows while the fix is on its way
  function lockLines(locked) {
    ['CartDrawer-CartItems', 'main-cart-items'].forEach(function (id) {
      var lines = document.getElementById(id);
      if (lines) lines.classList.toggle('cart__items--disabled', locked);
    });
  }

  function check(cart) {
    if (running) {
      again = true;
      return;
    }
    running = true;
    var latest = isWholeCart(cart) ? Promise.resolve(cart) : request('cart.js', { headers: { Accept: 'application/json' } });
    latest
      .then(function (current) {
        remember(current);
        var updates = isWholeCart(current) ? fixesFor(current) : null;
        if (!updates) return;
        lockLines(true);
        return request('cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            updates: updates,
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sections_url: window.location.pathname,
          }),
        }).then(function (fixed) {
          remember(fixed);
          refresh(fixed);
        });
      })
      .catch(function () {})
      .then(function () {
        lockLines(false);
        running = false;
        if (again) {
          again = false;
          check();
        }
      });
  }

  // A cart painted in another tab or before going back can be older than the real cart: repaint it first.
  // Only reads: the sections come from the Section Rendering API, and only when a form needs repainting.
  function sync() {
    return request('cart.js', { headers: { Accept: 'application/json' } })
      .then(function (cart) {
        remember(cart);
        var stale = cartForms().some(function (form) {
          return !formMatches(form, knownKeys);
        });
        if (!stale) {
          check(cart);
          return;
        }
        return fetch(window.location.pathname + '?sections=cart-drawer,cart-icon-bubble')
          .then(function (response) {
            if (!response.ok) throw new Error('Sections request failed');
            return response.json();
          })
          .then(function (sections) {
            cart.sections = sections;
            refresh(cart);
            check(cart);
          });
      })
      .catch(function () {});
  }

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.elements || !quantityFields(form).length) return;
      if (!formMatches(form, knownKeys)) {
        // Its lines no longer match the cart: refresh them and let the buyer submit again
        event.preventDefault();
        event.stopImmediatePropagation();
        sync();
        return;
      }
      correctForm(form);
    },
    true
  );

  document.addEventListener('submit', function (event) {
    if (!event.defaultPrevented) leaving = true;
  });

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') sync();
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (!event || event.source === SOURCE) return;
      remember(event.cartData);
      // The product offer section publishes the whole cart when it opens the drawer, and checking that costs no request.
      // Before going to checkout or to the cart page it publishes no cart, and the next page checks instead.
      if (event.source === 'custom-product-offer' && !isWholeCart(event.cartData)) return;
      check(event.cartData);
    });
  }

  // Coming back from checkout can restore this page from the back/forward cache with an older cart
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    leaving = false;
    sync();
  });

  // snippets/custom-gift-guard.liquid flags carts that already hold gifts when the page loads
  if (script && script.dataset.cartHasGifts === 'true') check();
})();
